import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { useSetupGuide } from "@/hooks/useSetupGuide";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { ScrollArea } from "@/components/ui/scroll-area";
import { WelcomeStep } from "./steps/WelcomeStep";
import { DefaultCurrencyStep } from "./steps/DefaultCurrencyStep";
import { CompletionStep } from "./steps/CompletionStep";
import { toast } from "sonner";
import { Loader2, ChevronLeft, ChevronRight, CheckCircle2 } from "lucide-react";
import { useSetupStepsConfig, SetupStep } from "@/hooks/useSetupStepsConfig";
import { useTranslation } from "react-i18next";
import { Skeleton } from "@/components/ui/skeleton";

interface SetupStepsProps {
  onComplete: () => void;
  initialStep?: string;
}

export function SetupSteps({ onComplete, initialStep }: SetupStepsProps) {
  const { t } = useTranslation();
  const { steps, isLoading: isLoadingSteps } = useSetupStepsConfig();
  const [currentStep, setCurrentStep] = useState("welcome");
  const [isCompleting, setIsCompleting] = useState(false);
  const { markSetupComplete } = useSetupGuide();
  const stepsContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isLoadingSteps && steps.length > 0) {
      if (initialStep && steps.find(s => s.id === initialStep)) {
        setCurrentStep(initialStep);
      } else {
        setCurrentStep(steps[0].id);
      }
    }
  }, [isLoadingSteps, steps, initialStep]);

  const currentIndex = steps.findIndex(step => step.id === currentStep);
  const isFirstStep = currentIndex === 0;
  const isLastStep = currentIndex === steps.length - 1;

  const handleNext = () => {
    if (isLastStep) {
      handleComplete();
      return;
    }
    setCurrentStep(steps[currentIndex + 1].id);
  };

  const handleBack = () => {
    if (isFirstStep) return;
    setCurrentStep(steps[currentIndex - 1].id);
  };

  const handleComplete = async () => {
    setIsCompleting(true);
    try {
      const success = await markSetupComplete();
      if (success) {
        toast.success(t('setupGuide.completedSuccess', 'Setup completed successfully!'));
        onComplete();
      } else {
        toast.error(t('setupGuide.completedError', 'Error marking setup as complete'));
      }
    } catch (error) {
      console.error("Error completing setup:", error);
      toast.error(t('setupGuide.error', 'An error occurred while completing setup'));
    } finally {
      setIsCompleting(false);
    }
  };

  const handleSkip = async () => {
    setIsCompleting(true);
    try {
      await markSetupComplete();
      toast.success(t('setupGuide.skippedSuccess', 'Setup skipped. You can access it anytime from Settings.'));
      onComplete();
    } catch (error) {
      console.error("Error skipping setup:", error);
    } finally {
      setIsCompleting(false);
    }
  };

  const getStepLabel = (step: SetupStep): string => {
    return t(step.labelKey, step.defaultLabel);
  };

  if (isLoadingSteps) {
    return (
      <div className="flex flex-col h-full p-6">
        <div className="space-y-4">
          <Skeleton className="h-4 w-full" />
          <div className="flex gap-2">
            <Skeleton className="h-8 w-20" />
            <Skeleton className="h-8 w-20" />
            <Skeleton className="h-8 w-20" />
          </div>
          <Skeleton className="h-40 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="px-6 pt-4 pb-4 bg-background">
        <div className="w-full bg-muted h-2 rounded-full mb-2">
          <div
            className="bg-primary h-2 rounded-full transition-all"
            style={{ width: `${(currentIndex / (steps.length - 1)) * 100}%` }}
          />
        </div>

        <div className="w-full overflow-x-auto pb-2 scrollbar-none" ref={stepsContainerRef}>
          <ToggleGroup
            type="single"
            value={currentStep}
            onValueChange={(value) => value && setCurrentStep(value)}
            className="flex space-x-1 min-w-max"
          >
            {steps.map((step, index) => (
              <ToggleGroupItem
                key={step.id}
                value={step.id}
                variant="outline"
                size="sm"
                disabled={isCompleting}
                className="whitespace-nowrap flex items-center gap-1"
              >
                {index < currentIndex ? (
                  <CheckCircle2 className="h-3 w-3 text-primary" />
                ) : (
                  <span className="flex h-4 w-4 items-center justify-center rounded-full bg-muted text-xs">
                    {index + 1}
                  </span>
                )}
                <span>{getStepLabel(step)}</span>
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
      </div>

      <div className="flex-1 overflow-hidden">
        <Tabs value={currentStep} className="h-full">
          <ScrollArea className="h-full">
            <div className="px-6 py-4">
              <TabsContent value="welcome" className="mt-0 data-[state=active]:block">
                <WelcomeStep />
              </TabsContent>

              <TabsContent value="default-currency" className="mt-0 data-[state=active]:block">
                <DefaultCurrencyStep />
              </TabsContent>

              <TabsContent value="completed" className="mt-0 data-[state=active]:block">
                <CompletionStep />
              </TabsContent>
            </div>
          </ScrollArea>
        </Tabs>
      </div>

      <div className="px-6 py-4 border-t bg-background">
        <div className="flex justify-between">
          <div>
            {!isFirstStep && (
              <Button
                variant="outline"
                onClick={handleBack}
                disabled={isCompleting}
                className="flex items-center"
              >
                <ChevronLeft className="mr-1 h-4 w-4" />
                {t('common.back', 'Back')}
              </Button>
            )}
          </div>

          <div className="flex space-x-2">
            {!isLastStep && (
              <Button
                variant="outline"
                onClick={handleSkip}
                disabled={isCompleting}
              >
                {t('common.skip', 'Skip')}
              </Button>
            )}

            <Button
              onClick={handleNext}
              disabled={isCompleting}
              className="flex items-center"
            >
              {isCompleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {isLastStep ? t('setupGuide.completing', 'Completing...') : t('setupGuide.processing', 'Processing...')}
                </>
              ) : (
                <>
                  {isLastStep ? t('setupGuide.complete', 'Complete Setup') : t('common.nextStep', 'Next Step')}
                  {!isLastStep && <ChevronRight className="ml-1 h-4 w-4" />}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
